import type { Layout, LayoutFamily, LayoutScope } from '@freealbum/document';
import { COVER_LAYOUTS } from './cover';
import { PAGE_LAYOUTS } from './page';
import { SPREAD_LAYOUTS } from './spread';

export { SPREAD_LAYOUTS } from './spread';
export { PAGE_LAYOUTS } from './page';
export { COVER_LAYOUTS } from './cover';
export * from './_helpers';

/** Every template, spread-scope first, cover last. Ids are stable; never renumber them. */
export const LAYOUTS: readonly Layout[] = [...SPREAD_LAYOUTS, ...PAGE_LAYOUTS, ...COVER_LAYOUTS];

/** Which part of the cover a cover template is for, from its slots' panels. */
export function coverLayoutPart(layout: Layout): 'front' | 'back' | 'wrap' {
  const panels = new Set(layout.slots.map((s) => s.panel ?? 'strip'));
  if (panels.size === 1 && panels.has('front')) return 'front';
  if (panels.size === 1 && panels.has('back')) return 'back';
  return 'wrap';
}

const BY_ID = new Map(LAYOUTS.map((l) => [l.id, l]));

export function getLayout(id: string): Layout | undefined {
  return BY_ID.get(id);
}

export interface LayoutQuery {
  /** exact number of photo slots */
  photoCount?: number;
  /** inclusive range, used by auto-fill when a group does not match a template exactly */
  minPhotos?: number;
  maxPhotos?: number;
  family?: LayoutFamily;
  scope?: LayoutScope;
  /** every tag must be present */
  tags?: string[];
  /** at least one text slot / no text slots */
  withText?: boolean;
}

/** Filter the catalogue. All criteria are ANDed; omitted criteria match everything. */
export function getLayouts(query: LayoutQuery = {}): Layout[] {
  return LAYOUTS.filter((l) => {
    if (query.photoCount !== undefined && l.photoCount !== query.photoCount) return false;
    if (query.minPhotos !== undefined && l.photoCount < query.minPhotos) return false;
    if (query.maxPhotos !== undefined && l.photoCount > query.maxPhotos) return false;
    if (query.family && !l.families.includes(query.family)) return false;
    if (query.scope && l.scope !== query.scope) return false;
    if (query.withText !== undefined && query.withText !== (l.textCount > 0)) return false;
    if (query.tags && !query.tags.every((t) => l.tags.includes(t))) return false;
    return true;
  });
}

/** Largest photoCount any inner-page template offers — the auto-fill group cap. */
export const MAX_LAYOUT_PHOTOS = LAYOUTS.filter((l) => l.scope !== 'cover').reduce((n, l) => Math.max(n, l.photoCount), 0);
